<script lang="ts" setup>
import { useTrackingStore } from '@dh-io-owpi/shared/src/stores/tracking';
import { useLoading } from '@dh-io-owpi/shared/src/composables/useLoading';
import type { Style } from '../../lib/api';
import type { SwitcherKind } from '../../lib/types/switchers';

defineProps<{
  styles: Style[];
  selectedStyle?: Style;
  switchStyle(style: Style): void;
  isSelected(style: Style): boolean;
  isDark: boolean;
  isExpanded: boolean;
}>();

const emit = defineEmits<{
  (event: 'expand', kind: SwitcherKind): void;
}>();

const { track } = useTrackingStore();

const open = () => {
  emit('expand', 'style');
  track({ timeout: 350, action: 'dynamic_stage|style_switcher', label: 'all_styles', category: 'feature' });
};

const { isLoading } = useLoading('stage');
</script>
<template>
  <div class="owpi-style-switcher-buttons" :class="{ 'owpi-style-switcher-buttons--dark': isDark }" v-bind="$attrs">
    <template v-if="isExpanded">
      <label
        v-for="style in styles"
        :key="style.id"
        :title="style.name"
        class="wb-button wb-button--secondary wb-button--medium wb-button--semi-transparent"
        :class="{ 'wb-button--theme-dark': isDark, 'wb-button-group__item--selected': isSelected(style) }"
        :disabled="isLoading ? 'true' : undefined"
      >
        <input type="radio" name="group" :checked="isSelected(style)" @change="switchStyle(style)" />
        <span class="owpi-style-switcher-buttons__label-text">{{ style.name }}</span>
      </label>
    </template>
    <button
      v-else
      type="button"
      :class="[
        'wb-button wb-button--secondary wb-button--medium wb-button--semi-transparent',
        { 'wb-button--theme-dark': isDark },
      ]"
      aria-label="Expand styles"
      @click="open"
    >
      {{ $t('dynamicStage.styleSwitcher.expandBtn') }}
    </button>
  </div>
</template>
<style lang="scss">
.owpi-style-switcher-buttons {
  &__label-text {
    @extend %ellipsis;
  }

  //// copy code from
  //// https://git.i.mercedes-benz.com/dh-io-workbench/core/blob/6.9.7/src/components/button-group/button-group.scss

  // used only for focus border in radio/checkbox variant
  --focus-color: var(--wb-blue-50);

  &--dark {
    --focus-color: var(--wb-blue-45);
  }

  display: flex;

  .wb-button + .wb-button {
    margin-left: -1px;
  }

  .wb-button:not(:last-child) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .wb-button:not(:first-child) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .wb-button:focus,
  .wb-button:focus-within {
    z-index: 1;
  }

  // variant with inputs

  label {
    position: relative;
  }

  input {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    margin: 0;
    padding: 0;
    opacity: 0;
    z-index: -1;
  }

  .wb-button.wb-button-group__item--selected {
    cursor: default;

    &:not(.wb-button--theme-dark) {
      &,
      &:focus,
      &:hover,
      &:active {
        animation: initial;
        background-color: var(--wb-black);
        border-color: var(--wb-grey-20);
        color: var(--wb-white);
      }
    }

    &.wb-button--theme-dark:not([disabled]) {
      &,
      &:focus,
      &:hover,
      &:active {
        animation: initial;
        background-color: var(--wb-white);
        border-color: var(--wb-grey-70);
        color: var(--wb-black);
      }
    }

    &:focus-within,
    &:focus-within:hover {
      box-shadow: 0 0 0 1px var(--focus-color) inset;
    }
  }
}
</style>
